import type { Meta, StoryObj } from '@storybook/angular';
import { expect, within } from 'storybook/test';
import { ButtonComponent } from './button.component';

const meta: Meta<ButtonComponent> = {
  title: 'Atoms/Buttons/Button',
  component: ButtonComponent,
  parameters: {
    docs: {
      description: {
        component: 'A reusable button component based on Supervin design system. Supports various styles and sizes.',
      },
    },
  },
  argTypes: {
    variant: {
      control: { type: 'select' },
      options: ['primary', 'secondary', 'light', 'red-light', 'red-dark', 'no-borders'],
      description: 'The visual style variant of the button',
    },
    size: {
      control: { type: 'select' },
      options: ['small', 'medium', 'large'],
      description: 'The size of the button',
    },
    disabled: {
      control: { type: 'boolean' },
      description: 'Whether the button is disabled',
    },
    fullWidth: {
      control: { type: 'boolean' },
      description: 'Whether the button should take full width',
    },
    type: {
      control: { type: 'select' },
      options: ['button', 'submit', 'reset'],
      description: 'The HTML button type',
    },
    buttonClick: {
      action: 'clicked',
      description: 'Callback fired when the button is clicked',
    },
  },
  args: {
    buttonClick: () => console.log('Button clicked'),
  },
};

export default meta;
type Story = StoryObj<ButtonComponent>;

// Primary Stories
export const Primary: Story = {
  args: {
    variant: 'primary',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Primary Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Primary Button')).toBeInTheDocument();
  },
};

export const Secondary: Story = {
  args: {
    variant: 'secondary',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Secondary Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Secondary Button')).toBeInTheDocument();
  },
};

export const Light: Story = {
  args: {
    variant: 'light',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Light Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Light Button')).toBeInTheDocument();
  },
};

export const RedLight: Story = {
  args: {
    variant: 'red-light',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Red Light Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Red Light Button')).toBeInTheDocument();
  },
};

export const RedDark: Story = {
  args: {
    variant: 'red-dark',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Red Dark Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Red Dark Button')).toBeInTheDocument();
  },
};

export const NoBorders: Story = {
  args: {
    variant: 'no-borders',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">No Borders Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('No Borders Button')).toBeInTheDocument();
  },
};

// Size Variations
export const Small: Story = {
  args: {
    variant: 'primary',
    size: 'small',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Small Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Small Button')).toBeInTheDocument();
  },
};

export const Large: Story = {
  args: {
    variant: 'primary',
    size: 'large',
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Large Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Large Button')).toBeInTheDocument();
  },
};

// State Variations
export const Disabled: Story = {
  args: {
    variant: 'primary',
    disabled: true,
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Disabled Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Disabled Button')).toBeInTheDocument();
  },
};

export const FullWidth: Story = {
  args: {
    variant: 'primary',
    fullWidth: true,
  },
  render: (args) => ({
    props: args,
    template: '<app-button [variant]="variant" [size]="size" [disabled]="disabled" [fullWidth]="fullWidth" [type]="type" (buttonClick)="buttonClick($event)">Full Width Button</app-button>',
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText('Full Width Button')).toBeInTheDocument();
  },
};

// All Variants Demo
export const AllVariants: Story = {
  render: () => ({
    template: `
      <div style="display: flex; flex-direction: column; gap: 1rem; max-width: 400px;">
        <app-button variant="primary" (buttonClick)="buttonClick($event)">Primary Button</app-button>
        <app-button variant="secondary" (buttonClick)="buttonClick($event)">Secondary Button</app-button>
        <app-button variant="light" (buttonClick)="buttonClick($event)">Light Button</app-button>
        <app-button variant="red-light" (buttonClick)="buttonClick($event)">Red Light Button</app-button>
        <app-button variant="red-dark" (buttonClick)="buttonClick($event)">Red Dark Button</app-button>
        <app-button variant="no-borders" (buttonClick)="buttonClick($event)">No Borders Button</app-button>
      </div>
    `,
    props: {
      buttonClick: () => console.log('Button clicked'),
    },
  }),
  parameters: {
    docs: {
      description: {
        story: 'A showcase of all available button variants.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelectorAll('app-button').length).toBe(6);
  },
};
